import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';

/**
 * useShowMore — caps a list to `initial` rows and reveals more in `chunk`-sized batches.
 *
 * Usage:
 *   const { visibleItems, hasMore, remaining, showMore, reset, ShowMoreButton } = useShowMore(rows, 10, 10);
 *   {visibleItems.map(...)}
 *   <ShowMoreButton />
 */
export const useShowMore = (items, initial = 10, chunk = 10) => {
    const [limit, setLimit] = useState(initial);
    const total = items?.length || 0;

    // Reset visible count whenever the underlying list shrinks below current limit
    useEffect(() => {
        if (limit > initial && total < limit) setLimit(Math.max(initial, total));
    }, [total, limit, initial]);

    const visibleItems = (items || []).slice(0, limit);
    const hasMore = total > limit;
    const remaining = Math.max(0, total - limit);
    const expanded = limit > initial && total > initial;
    const showMore = () => setLimit((l) => l + chunk);
    const showAll = () => setLimit(total || initial);
    const reset = () => setLimit(initial);

    const ShowMoreButton = ({ label, className = '', testid = 'show-more-btn' }) => {
        if (!hasMore && !expanded) return null;
        return (
            <div className={`flex flex-wrap justify-center items-center gap-2 mt-3 ${className}`}>
                {hasMore && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={showMore}
                        data-testid={testid}
                        className="border-white/15 text-[#A0AAB2] hover:bg-white/5 hover:text-white text-xs"
                    >
                        {label || `Show ${Math.min(chunk, remaining)} more`}
                        <ChevronDown className="w-3.5 h-3.5 ml-1" />
                    </Button>
                )}
                {hasMore && total > limit + chunk && (
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={showAll}
                        data-testid={`${testid}-all`}
                        className="text-[#FFD700] hover:bg-[#FFD700]/10 text-xs h-9"
                    >
                        Show all ({total})
                    </Button>
                )}
                {expanded && (
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={reset}
                        data-testid={`${testid}-collapse`}
                        className="text-[#A0AAB2] hover:bg-white/5 hover:text-white text-xs h-9"
                    >
                        <ChevronUp className="w-3.5 h-3.5 mr-1" />
                        Collapse
                    </Button>
                )}
            </div>
        );
    };

    return { visibleItems, hasMore, remaining, showMore, showAll, reset, ShowMoreButton, total, limit, expanded };
};

export default useShowMore;
